export const DELAY = 750

/**
 * Debounce a function and allow a value to be returned as a promise. The promise will be resolved after the wait time has expired.
 *
 * @param funcToDebounce The function that should be debounced.
 * @param delay The amount of time to wait before resolving the promise. Every call to the returned function will reset the countdown.
 */
export function debounceWithReturn<TFunc extends (...args: never[]) => unknown>(
  funcToDebounce: (...args: Parameters<TFunc>) => unknown,
  delay: number
): (...args: Parameters<TFunc>) => Promise<unknown> {
  let timeoutId: number | undefined = undefined

  const debouncedFunc = (...argsFromCall: Parameters<TFunc>): Promise<unknown> => {
    if (timeoutId != undefined) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }

    return new Promise<unknown>((resolve, reject) => {
      timeoutId = window.setTimeout(() => {
        try {
          resolve(funcToDebounce(...argsFromCall))
        } catch (error) {
          reject(error)
        }
      }, delay)
    })
  }

  return debouncedFunc
}
